import Image from 'next/image'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'

import type { PublicUserProfile } from 'codegen/types'
import { VerifiedPremiumRibbon } from '@web/app/[locale]/clubhouse/[slug]/_components/verified-premium-ribbon'
import type { Project } from '../page-client'
import { ProjectMoreMenu } from './project-more-menu'
import { IconPhotoPrints } from '@repo/design-system-cf/components/icons'
import { useFormatTimeSpan } from '../_hooks/use-format-time-span'
import * as React from 'react'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import { CopyUserIdButton } from './copy-user-id-button'

// oxlint-disable-next-line complexity
export const ProjectPostMemberCard = ({
  user,
  project,
  showViewPostLink,
  inCommentMode,
  inPhotoViewMode,
}: {
  user: PublicUserProfile | undefined
  project: Project
  showViewPostLink?: boolean
  inCommentMode?: boolean
  inPhotoViewMode?: boolean
}) => {
  const { breakpoint } = useBreakpoint()
  const formatTimeSpan = useFormatTimeSpan()
  const timeElapsed = React.useMemo(
    () =>
      project.createTime &&
      formatTimeSpan(new Date().getTime() - new Date(project.createTime).getTime()),
    [project.createTime, formatTimeSpan],
  )

  if (!user) {
    return null
  }

  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')

  return (
    <div className="flex w-full flex-col items-stretch gap-3 pb-2 relative">
      {isMobile ? (
        <div className="w-full flex flex-row items-center justify-between">
          <div className="bg-cf-sky-blue-50 w-fit h-5 flex flex-row justify-start gap-0.5 items-center font-semibold border text-cf-sky-blue-800 py-0.5 px-1.5 border-[#1313151F] text-[11px] rounded-[4px]">
            <IconPhotoPrints className="size-3" /> {'Project'}
          </div>
          <div className="flex flex-row items-center justify-start">
            <p className="text-12 text-cf-grey-500 text-nowrap mr-1.5 block ">{timeElapsed}</p>

            {!inCommentMode ? (
              <ProjectMoreMenu
                project={project}
                showViewPostLink={showViewPostLink}
                inPhotoViewMode={inPhotoViewMode}
              />
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="flex flex-row items-center justify-start gap-3">
        <Link
          href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${user?.slug}`}
          target="_blank"
          className="size-10 min-w-10 rounded-full relative"
          data-testid="project-post-member-card-avatar"
        >
          <Image
            src={user?.avatarUrl || `https://avatar.iran.liara.run/public/${1}`}
            alt="logo"
            width={40}
            height={40}
            className="overflow-hidden rounded-full"
          />
          <div className="absolute inset-0 rounded-full border border-[#1313151F]" />
        </Link>

        <div className="flex flex-grow flex-col">
          <div className="flex h-full flex-row items-center justify-start gap-1">
            <div className="flex flex-col items-start gap-0.5 text-[14px] leading-[20px] text-gray-100 w-full">
              <div className="flex flex-col tablet:flex-row items-start sm:gap-2">
                <div className="flex text-14 justify-start w-full font-semibold">
                  <Link
                    href={`${AppRoutes.SOCIAL_FEED_CRAFTER}/${user?.slug}`}
                    target="_blank"
                    className="min-w-0 break-words [word-break:break-word]"
                    data-testid="project-post-member-card-username"
                  >
                    {user?.firstName || 'User'} {user?.lastName || ''}
                  </Link>
                  <VerifiedPremiumRibbon user={user! as PublicUserProfile} />
                </div>
                <CopyUserIdButton userId={user?.id} />
              </div>
              <span className="text-cf-grey-600 max-w-[120px] truncate text-[12px] font-normal leading-4 md:max-w-[300px]  ">
                {user?.superSkill || ''}
              </span>
            </div>
            <p className="text-12 text-cf-grey-500 text-nowrap mr-3 hidden md:block">
              {timeElapsed}
            </p>

            {project?.type === 'product' && !inCommentMode ? (
              <>
                <div className="bg-cf-sky-blue-50 h-5 md:h-[26px] hidden tablet:flex flex-row justify-start gap-1  text-[11px] md:text-14 items-center font-semibold border text-cf-sky-blue-800 py-1 px-2 border-[#1313151F] rounded-2">
                  <IconPhotoPrints className="size-4" /> {'Project'}
                </div>
              </>
            ) : null}
            {!isMobile ? (
              <div>
                {!inCommentMode ? (
                  <ProjectMoreMenu
                    project={project}
                    showViewPostLink={showViewPostLink}
                    inPhotoViewMode={inPhotoViewMode}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
